import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  fetchVariantExportPayload,
  downloadPngFromVariantImageResponse,
  downloadPdfFromDataUrl,
} from "@/lib/qrDownload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Image, FileImage, FileCode, FileText, Crosshair, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EXPORT_OPTIONS = [
  { id: "standard", label: "Standard PNG", desc: "Web & social sharing", icon: Image, size: 10, format: "png" },
  { id: "print", label: "High-Res PNG", desc: "Print-quality, 300+ DPI", icon: FileImage, size: 40, format: "png" },
  { id: "svg", label: "SVG Vector", desc: "Scalable, any size", icon: FileCode, size: 10, format: "svg", needs: "export_svg" },
  { id: "pdf", label: "PDF Document", desc: "Print-ready single page", icon: FileText, size: 10, format: "pdf", needs: "export_pdf" },
  { id: "engrave", label: "Engraving Safe", desc: "Laser & CNC ready", icon: Crosshair, size: 14, format: "png", engraving: true, needs: "creative_qr" },
];

export default function QrExportModal({ open, onClose, variant }) {
  const { entitlements, entitlementsReady } = useAuth();
  const ents = entitlements || {};
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState({});

  const isLocked = (opt) => !entitlementsReady || (opt.needs && !ents[opt.needs]);

  const loadPreviewPayload = async (option) => {
    const payload = await fetchVariantExportPayload(api, variant, option);
    setPreviews((p) => ({ ...p, [option.id]: payload }));
    return payload;
  };

  const generatePreview = async (option) => {
    if (isLocked(option) || previews[option.id] || option.format === "pdf") return;
    setLoading((p) => ({ ...p, [option.id]: true }));
    try {
      await loadPreviewPayload(option);
    } catch {
      toast.error("Failed to generate preview");
    } finally {
      setLoading((p) => ({ ...p, [option.id]: false }));
    }
  };

  const handleDownload = async (option) => {
    if (isLocked(option)) {
      toast.error("Upgrade required for this export option");
      return;
    }
    let data = previews[option.id];
    if (!data) {
      setLoading((p) => ({ ...p, [option.id]: true }));
      try {
        data = await loadPreviewPayload(option);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to prepare download");
        return;
      } finally {
        setLoading((p) => ({ ...p, [option.id]: false }));
      }
    }
    if (!data?.data) {
      toast.error("No image data returned from server");
      return;
    }

    const safeName = (variant.name || "qr").replace(/[^a-zA-Z0-9-_]/g, "-").toLowerCase();

    try {
      if (data.format === "svg") {
        const blob = new Blob([data.data], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${safeName}.svg`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else if (data.format === "pdf") {
        downloadPdfFromDataUrl(data.data, `${safeName}.pdf`);
      } else {
        downloadPngFromVariantImageResponse(data, `${safeName}_${option.id}.png`);
      }
      toast.success(`Downloaded ${option.label}`);
    } catch (e) {
      console.error("[QrExportModal] download failed", e);
      toast.error(e?.message || "Download failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="export-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Export QR Code</DialogTitle>
          <p className="text-xs text-muted-foreground">{variant?.name}</p>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          {EXPORT_OPTIONS.map((opt) => {
            const locked = isLocked(opt);
            return (
            <div
              key={opt.id}
              className={`flex items-center gap-3 p-3 rounded-lg border border-border/60 transition-all group ${
                locked ? "opacity-60 cursor-not-allowed" : "hover:border-primary/40 hover:bg-primary/5 cursor-pointer"
              }`}
              onMouseEnter={() => generatePreview(opt)}
              onClick={() => handleDownload(opt)}
              data-testid={`export-option-${opt.id}`}
            >
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center shrink-0 overflow-hidden border border-border/30">
                {loading[opt.id] ? (
                  <Loader2 size={16} className="animate-spin text-muted-foreground" />
                ) : previews[opt.id] && opt.format !== "pdf" ? (
                  opt.format === "svg" ? (
                    <div className="w-10 h-10" dangerouslySetInnerHTML={{ __html: previews[opt.id].data.substring(0, 5000) }} />
                  ) : (
                    <img src={previews[opt.id].data} alt="" className="w-10 h-10 object-contain" />
                  )
                ) : locked ? (
                  <Lock size={16} className="text-muted-foreground" />
                ) : (
                  <opt.icon size={18} className="text-muted-foreground" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium flex items-center gap-2">
                  {opt.label}
                  {opt.id === "print" && <Badge variant="outline" className="text-[10px] py-0">Best for Print</Badge>}
                  {opt.id === "engrave" && <Badge variant="outline" className="text-[10px] py-0">Laser Safe</Badge>}
                  {locked && <Badge variant="outline" className="text-[10px] py-0">Upgrade</Badge>}
                </p>
                <p className="text-[11px] text-muted-foreground">{opt.desc}</p>
              </div>

              {locked ? (
                <Link to="/dashboard/subscription" onClick={(e) => e.stopPropagation()} className="text-xs text-primary shrink-0">
                  Upgrade
                </Link>
              ) : (
                <Download size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              )}
            </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
